import { useState, useEffect } from 'react';
import './BlogPage.css';

const POSTS = [
  // I TRE ARTICOLI DEL 13/09/2026. Hanno `href` e non `content`: sono pagine
  // HTML vere sotto /articles/, scritte in italiano e in inglese, non testo
  // incollato qui dentro. PostCard se ne accorge dal campo e apre il link
  // invece di aprire l'articolo dentro l'applicazione.
  {
    slug: 'quanto-e-un-terabyte',
    href: '/articles/quanto-e-un-terabyte.html',
    title: 'Quanto è 1 TB? E 2 TB?',
    date: '2026-09-13',
    category: 'Guida',
    readTime: '4 min',
    excerpt: 'Un terabyte in foto, canzoni, documenti e ore di video — e il modo pratico per capire quanto spazio ti serve davvero.',
  },
  {
    slug: 'condividere-file-senza-consegnarli',
    href: '/articles/condividere-file-senza-consegnarli.html',
    title: 'Condividere un file senza consegnarlo a noi',
    date: '2026-09-13',
    category: 'Come funziona',
    readTime: '5 min',
    excerpt: 'La chiave viaggia dopo il cancelletto, e quella parte dell\'indirizzo al nostro server non arriva mai. Scadenza, password e limite di scaricamenti.',
  },
  {
    slug: 'cifrato-non-vuol-dire-illeggibile',
    href: '/articles/cifrato-non-vuol-dire-illeggibile.html',
    title: 'Cifrato non vuol dire che non possono leggerlo',
    date: '2026-09-13',
    category: 'Privacy',
    readTime: '6 min',
    excerpt: 'Google, Dropbox e Apple cifrano tutti. La domanda vera è chi tiene la chiave — con le fonti ufficiali di ciascuno.',
  },
  {
    slug: 'cos-e-la-crittografia-zero-knowledge',
    title: 'Cos\'è la crittografia Zero-Knowledge e perché protegge davvero i tuoi file',
    date: '2026-04-10',
    category: 'Sicurezza',
    readTime: '5 min',
    excerpt: 'La crittografia zero-knowledge è il sistema che garantisce che nemmeno il server possa leggere i tuoi file. Scopri come funziona in modo semplice.',
    content: `
## Cos'è la crittografia Zero-Knowledge?

La crittografia **zero-knowledge** è un approccio alla sicurezza informatica in cui il fornitore del servizio — in questo caso LifetimeCloud — non ha mai accesso ai dati in chiaro degli utenti. La parola "zero" si riferisce alla quantità di conoscenza che il server acquisisce sul contenuto dei file: zero.

Questo è fondamentalmente diverso da servizi come Google Drive o Dropbox, dove i tuoi file vengono caricati in chiaro (o con cifratura che il servizio può annullare) e potrebbero essere letti dagli amministratori, da hacker, o su richiesta governativa.

## Come funziona in pratica?

Il processo è semplice da comprendere:

**1. La chiave nasce dalla tua password**
Quando accedi a LifetimeCloud, il tuo browser trasforma la password in una chiave crittografica usando PBKDF2 con 310.000 iterazioni. Questa chiave non lascia mai il dispositivo — esiste solo nella RAM del browser.

**2. Il file viene cifrato prima dell'invio**
Prima che il file tocchi internet, il browser lo cifra con AES-256-GCM. Solo il blob cifrato — dati incomprensibili senza la chiave — viene inviato al server.

**3. Il server è cieco**
Il nostro server riceve e conserva solo dati cifrati, e la chiave non viene mai salvata da noi. Anche in caso di violazione dei nostri server, quello che un attaccante troverebbe sono blocchi illeggibili senza la password dell'utente.

## Perché è importante?

In un mondo dove le violazioni dei dati sono quotidiane, affidarsi a un servizio che non conserva la tua chiave sposta la protezione dalla fiducia alla matematica: quello che viene archiviato resta illeggibile senza la tua password.

Con LifetimeCloud, anche se volessimo leggere i tuoi file, non potremmo. La privacy non è una promessa: è un'architettura.
    `
  },
  {
    slug: 'aes-256-gcm-spiegato-semplice',
    title: 'AES-256-GCM: lo standard militare che protegge i tuoi file su LifetimeCloud',
    date: '2026-04-15',
    category: 'Tecnologia',
    readTime: '6 min',
    excerpt: 'AES-256-GCM è lo stesso algoritmo usato da banche e governi. Ecco cosa significa e perché è praticamente inviolabile.',
    content: `
## Cosa significa AES-256-GCM?

**AES** (Advanced Encryption Standard) è l'algoritmo di cifratura simmetrica più diffuso al mondo, adottato dal governo degli Stati Uniti nel 2001 e oggi standard de facto per la protezione dei dati sensibili.

**256** indica la lunghezza della chiave in bit. Una chiave a 256 bit ha 2²⁵⁶ possibili combinazioni — un numero così grande che anche con tutti i computer del mondo, ci vorrebbero miliardi di miliardi di anni per provarle tutte con forza bruta.

**GCM** (Galois/Counter Mode) è la modalità operativa che aggiunge l'autenticazione al processo di cifratura. Questo significa che non solo i dati sono cifrati, ma è anche impossibile modificarli senza che la decifratura fallisca — garantendo integrità oltre alla riservatezza.

## Come lo usa LifetimeCloud?

LifetimeCloud implementa AES-256-GCM direttamente nel browser tramite le **Web Crypto API native** — le interfacce crittografiche standard di tutti i browser moderni. Non usiamo librerie di terze parti per la crittografia: il browser stesso gestisce le operazioni crittografiche.

Per ogni file caricato:
- Viene generato un **vettore iniziale (IV) casuale** di 96 bit
- Il file viene cifrato con la chiave AES-256 dell'utente e l'IV
- Solo il blob cifrato + l'IV vengono inviati al server

L'IV è necessario per la decifratura ma non rivela nulla sul contenuto — è come il numero seriale di una cassaforte.

## Perché GCM e non altri modi?

Esistono diverse modalità operative per AES (CBC, CTR, ECB...). GCM è preferita perché:
- Garantisce **autenticazione**: rileva qualsiasi manomissione dei dati
- È **parallelizzabile**: performante su hardware moderno  
- È **standardizzata**: ampiamente analizzata e testata dalla comunità crittografica
    `
  },
  {
    slug: 'pbkdf2-come-funziona-derivazione-chiave',
    title: 'PBKDF2: come la tua password diventa una chiave crittografica infrangibile',
    date: '2026-04-20',
    category: 'Tecnologia',
    readTime: '5 min',
    excerpt: 'La derivazione della chiave è il processo che trasforma la tua password in una chiave AES-256. PBKDF2 rende questo processo resistente agli attacchi brute-force.',
    content: `
## Il problema delle password

Le password sono scelte dagli esseri umani e quindi tendono ad essere prevedibili. Un attacco brute-force su una password tipica richiederebbe pochi secondi con hardware moderno.

La **derivazione della chiave** risolve questo problema trasformando la password in una chiave crittografica tramite un processo deliberatamente lento e costoso.

## Cosa fa PBKDF2?

PBKDF2 (Password-Based Key Derivation Function 2) applica ripetutamente una funzione hash crittografica alla password, mescolata con un **salt** casuale, per un numero configurabile di iterazioni.

LifetimeCloud usa **310.000 iterazioni** — il numero consigliato da NIST (l'ente americano per gli standard tecnologici) nel 2023. Ogni iterazione richiede calcolo: più iterazioni, più tempo ci vuole per verificare una password.

## Perché il salt?

Il salt è un valore casuale unico per ogni utente, generato al momento della registrazione. Serve a impedire gli **attacchi rainbow table** — database pre-calcolati di hash di password comuni.

Con il salt, anche se due utenti usassero la stessa password, le loro chiavi crittografiche sarebbero completamente diverse.

## Quanto è sicuro?

Con 310.000 iterazioni, tentare 1 miliardo di password al secondo richiederebbe oltre 300.000 anni per esaminare tutte le possibili combinazioni di una password di 12 caratteri.

In pratica: finché la tua password è ragionevolmente complessa (almeno 10 caratteri con lettere e numeri), è impossibile forzarla.
    `
  },
  {
    slug: 'differenza-cloud-normale-vs-zero-knowledge',
    title: 'Cloud normale vs Zero-Knowledge: qual è la differenza vera?',
    date: '2026-04-25',
    category: 'Privacy',
    readTime: '7 min',
    excerpt: 'Google Drive, Dropbox e OneDrive possono leggere i tuoi file. LifetimeCloud no. Ecco perché la differenza è fondamentale per la tua privacy.',
    content: `
## Come funzionano i cloud tradizionali

Quando carichi un file su Google Drive, Dropbox o iCloud, il file viene inviato al server in forma leggibile (o con cifratura che il servizio controlla). Questo significa che:

- **Gli amministratori del servizio** possono accedere ai tuoi file
- **In caso di violazione** dei server, i tuoi dati potrebbero essere esposti
- **Le autorità** possono richiedere l'accesso ai tuoi dati con un'ordinanza
- **Il servizio** può scansionare i tuoi file per scopi pubblicitari o di machine learning

Molti servizi affermano di cifrare i dati "a riposo" — ma la chiave di cifratura è nelle loro mani, non nelle tue.

## Come funziona LifetimeCloud

LifetimeCloud adotta un approccio radicalmente diverso:

**La cifratura avviene nel tuo browser, prima del caricamento.** Il server non riceve mai il file in chiaro — solo un blob di dati cifrati incomprensibili.

La chiave di cifratura viene creata nel tuo browser a partire dalla tua password, e non lascia mai il tuo dispositivo: LifetimeCloud non la conserva e non la riceve. La password invece passa dai nostri server quando accedi, per essere verificata — è il modo in cui funziona qualunque accesso web, e per questo la chiave non coincide con la password ma ne è derivata sul tuo dispositivo.

## Tabella comparativa

| Caratteristica | Google Drive | Dropbox | LifetimeCloud |
|---|---|---|---|
| Cifratura a riposo | ✅ | ✅ | ✅ |
| Chiave controllata dall'utente | ❌ | ❌ | ✅ |
| Il servizio può leggere i file | ✅ | ✅ | ❌ |
| Cifratura end-to-end | ❌ | ❌ | ✅ |
| Accesso governativo possibile | ✅ | ✅ | ❌ |

## Quando ha senso usare LifetimeCloud?

LifetimeCloud è ideale per:
- Documenti personali sensibili (estratti conto, documenti d'identità)
- File aziendali riservati
- Backup di dati importanti
- Chiunque voglia la certezza matematica che i propri dati siano privati
    `
  },
  {
    slug: 'come-proteggere-dati-online',
    title: '10 modi per proteggere i tuoi dati online nel 2026',
    date: '2026-05-01',
    category: 'Guida',
    readTime: '8 min',
    excerpt: 'Password manager, 2FA, cloud cifrato: ecco i 10 passi concreti per proteggere davvero la tua privacy digitale.',
    content: `
## 1. Usa un password manager

Mai riusare la stessa password. Un password manager come Bitwarden (open source, gratuito) genera e ricorda password uniche per ogni sito.

## 2. Attiva l'autenticazione a due fattori (2FA)

Il 2FA aggiunge un secondo livello di verifica oltre alla password. Usa un'app come Google Authenticator o Authy, non gli SMS (vulnerabili al SIM swapping).

## 3. Usa un cloud zero-knowledge per i file sensibili

I cloud tradizionali come Google Drive possono leggere i tuoi file. Usa LifetimeCloud o servizi equivalenti per documenti sensibili.

## 4. Cifra il disco del tuo computer

Windows: BitLocker. Mac: FileVault. Linux: LUKS. La cifratura del disco protegge i dati in caso di furto del dispositivo.

## 5. Usa una VPN affidabile

Una VPN cifra il traffico internet e nasconde il tuo indirizzo IP. Scegli un provider che non conserva i log (ProtonVPN, Mullvad).

## 6. Aggiorna sempre il software

La maggior parte degli attacchi sfrutta vulnerabilità note in software non aggiornato. Attiva gli aggiornamenti automatici.

## 7. Usa un browser privacy-oriented

Firefox con uBlock Origin, o Brave. Evita browser che tracciano le tue attività per scopi pubblicitari.

## 8. Controlla le autorizzazioni delle app

App che richiedono accesso a fotocamera, microfono, posizione senza motivo sono un rischio. Revisiona periodicamente le autorizzazioni sul tuo smartphone.

## 9. Usa email cifrate per comunicazioni sensibili

ProtonMail e Tutanota offrono email end-to-end cifrate. Per comunicazioni ultra-sensibili, considera Signal.

## 10. Fai backup regolari

Il backup è la difesa finale contro ransomware, guasti hardware e errori umani. Segui la regola 3-2-1: 3 copie, su 2 supporti diversi, 1 offsite.
    `
  },
  {
    slug: 'webdav-rclone-guida',
    title: 'Come usare LifetimeCloud con rclone e WebDAV: guida completa',
    date: '2026-05-03',
    category: 'Guida tecnica',
    readTime: '6 min',
    excerpt: 'LifetimeCloud supporta WebDAV e rclone per integrare il cloud cifrato con il tuo sistema operativo e automatizzare i backup.',
    content: `
## Cos'è WebDAV?

WebDAV (Web Distributed Authoring and Versioning) è un protocollo che estende HTTP permettendo di gestire file su un server remoto come se fossero su un disco locale. LifetimeCloud supporta WebDAV per permettere l'integrazione con client di terze parti.

## Configurare rclone con LifetimeCloud

rclone è uno strumento a riga di comando potente per sincronizzare file con servizi cloud. Ecco come configurarlo:

\`\`\`bash
# Installa rclone
curl https://rclone.org/install.sh | sudo bash

# Configura il remote
rclone config
\`\`\`

Scegli **WebDAV** come tipo, poi inserisci:
- URL: \`https://lifetimecloud.me/dav/tuousername/\`
- Vendor: Other
- User: il tuo username LifetimeCloud
- Password: il token WebDAV (dal profilo)

## Comandi utili

\`\`\`bash
# Lista file
rclone ls lifetimecloud:

# Sincronizza una cartella locale
rclone sync /percorso/locale lifetimecloud:Documenti

# Monta come disco (Linux/Mac)
rclone mount lifetimecloud: ~/LifetimeCloud --daemon

# Backup automatico
rclone sync ~/Documenti lifetimecloud:Backup/Documenti
\`\`\`

## Automazione con cron

\`\`\`bash
# Backup automatico ogni notte alle 2:00
0 2 * * * rclone sync ~/Documenti lifetimecloud:Backup/Documenti
\`\`\`

## Nota sulla cifratura

Importante: quando usi WebDAV o rclone, i file vengono caricati **senza la cifratura end-to-end del browser**. Il protocollo WebDAV non implementa la cifratura lato client di LifetimeCloud. Usa WebDAV per backup e sincronizzazione dove la semplicità è prioritaria; usa l'interfaccia web per i file più sensibili.
    `
  },
  {
    slug: 'gdpr-cloud-storage-italia',
    title: 'GDPR e cloud storage: cosa devi sapere per essere in regola in Italia',
    date: '2026-05-05',
    category: 'Privacy & Legge',
    readTime: '7 min',
    excerpt: 'Il GDPR regola il trattamento dei dati personali. Ecco cosa significa per chi usa servizi cloud e perché la cifratura end-to-end può fare la differenza.',
    content: `
## Cosa dice il GDPR sul cloud storage?

Il Regolamento Generale sulla Protezione dei Dati (GDPR) entrato in vigore nel 2018 stabilisce regole precise sul trattamento dei dati personali nell'Unione Europea.

Per chi usa servizi cloud per conservare dati personali (propri o di terzi), il GDPR richiede:

**Sicurezza adeguata**: i dati devono essere protetti con misure tecniche appropriate, tra cui la cifratura.

**Minimizzazione**: raccogliere solo i dati strettamente necessari.

**Trasparenza**: sapere dove sono conservati i dati e chi vi ha accesso.

## Il problema dei cloud americani

Servizi come Google Drive, Dropbox e OneDrive sono gestiti da aziende americane soggette al **CLOUD Act** — una legge che obbliga le aziende USA a fornire dati alle autorità americane anche se i server sono in Europa.

Questo crea una tensione con il GDPR: i tuoi dati potrebbero essere trasferiti fuori dall'UE senza il tuo consenso.

## Come la cifratura end-to-end risolve il problema

Con un servizio zero-knowledge come LifetimeCloud:

- Il server conserva solo dati cifrati
- Anche in caso di richiesta legale, non ci sono dati leggibili da fornire
- La cifratura è una "misura tecnica adeguata" ai sensi del GDPR

**Importante**: LifetimeCloud è un servizio italiano con server in Europa, soggetto esclusivamente al diritto italiano ed europeo.

## Consigli pratici

Per essere in regola con il GDPR quando usi servizi cloud:
1. Preferisci servizi con server in Europa
2. Usa servizi che cifrano i dati end-to-end
3. Leggi l'informativa privacy prima di caricare dati di terzi
4. Documenta le misure di sicurezza adottate
    `
  },
  {
    slug: 'autenticazione-due-fattori-2fa-guida',
    title: 'Autenticazione a due fattori (2FA): guida completa per proteggere il tuo account',
    date: '2026-05-07',
    category: 'Sicurezza',
    readTime: '5 min',
    excerpt: 'Il 2FA aggiunge un secondo livello di protezione al tuo account. Ecco come funziona, perché è essenziale e come attivarlo su LifetimeCloud.',
    content: `
## Perché la password da sola non basta

Le password possono essere rubate in molti modi: phishing, violazioni di database, malware, shoulder surfing. Se un malintenzionato ottiene la tua password, senza un secondo fattore può accedere immediatamente al tuo account.

L'autenticazione a due fattori (2FA) aggiunge un secondo elemento di verifica: qualcosa che **hai** (il telefono) oltre a qualcosa che **sai** (la password).

## Come funziona il TOTP?

LifetimeCloud usa TOTP (Time-based One-Time Password) — lo standard aperto usato da Google Authenticator, Authy e altri.

Il funzionamento è semplice:
1. Alla configurazione, LifetimeCloud e la tua app condividono un **segreto** (mostrato come QR code)
2. Ogni 30 secondi, sia LifetimeCloud che l'app calcolano un codice a 6 cifre basato sul segreto e sull'ora
3. I due codici devono coincidere per autenticarsi

Il codice cambia ogni 30 secondi e può essere usato una sola volta — anche se qualcuno lo intercetta, non serve a nulla dopo 30 secondi.

## Come attivare il 2FA su LifetimeCloud

1. Accedi a LifetimeCloud → Profilo
2. Nella sezione "Verifica in due passaggi" clicca **Attiva 2FA**
3. Scansiona il QR code con Google Authenticator o Authy
4. Inserisci il codice a 6 cifre per confermare
5. Salva il codice di backup in un posto sicuro

## App consigliate

- **Authy**: supporta backup cifrato, multi-dispositivo
- **Google Authenticator**: semplice e affidabile
- **Aegis** (Android, open source): ottima privacy
- **Raivo** (iOS): open source, iCloud backup

## Cosa fare se perdi il telefono?

Hai due opzioni:
1. **Codice di backup**: LifetimeCloud genera un token di recupero durante la registrazione — salvalo
2. **Recovery token**: usa il token master di recupero per accedere all'account

Per questo motivo è fondamentale salvare il token di recupero in un posto sicuro prima di attivare il 2FA.
    `
  },
  {
    slug: 'backup-sicuro-file-importanti',
    title: 'Strategia di backup sicura: come non perdere mai i tuoi file importanti',
    date: '2026-05-08',
    category: 'Guida',
    readTime: '6 min',
    excerpt: 'La regola 3-2-1 del backup, cloud cifrato e automazione: ecco la strategia completa per proteggere i tuoi dati da qualsiasi scenario.',
    content: `
## Perché il backup è fondamentale

I dati possono andare persi per mille motivi: guasto hardware, furto, ransomware, cancellazione accidentale, incendio, alluvione. La domanda non è *se* perderai dati, ma *quando*.

Un sistema di backup corretto ti permette di recuperare da qualsiasi scenario in pochi minuti invece di piangere su anni di lavoro perso.

## La regola 3-2-1

La strategia di backup più diffusa tra i professionisti della sicurezza è la **regola 3-2-1**:

- **3** copie dei dati
- su **2** supporti diversi (es. disco interno + disco esterno)
- di cui **1** offsite (fuori dalla tua sede fisica)

Il cloud cifrato come LifetimeCloud è perfetto per la copia offsite — i tuoi file sono fisicamente altrove e protetti da cifratura.

## Implementazione pratica

**Livello 1 — Locale**
Il tuo computer o NAS. Backup automatico quotidiano con Time Machine (Mac) o File History (Windows).

**Livello 2 — Disco esterno**
Un disco esterno tenuto in casa o in ufficio. Sincronizzazione settimanale.

**Livello 3 — Cloud cifrato**
LifetimeCloud o servizio equivalente. Backup dei file più importanti con cifratura end-to-end.

## Automazione con rclone

\`\`\`bash
# Backup automatico su LifetimeCloud ogni notte
0 2 * * * rclone sync ~/Documenti lifetimecloud:Backup/$(date +%Y-%m)
\`\`\`

## Cosa fare dopo un backup

Un backup non verificato non vale nulla. Periodicamente:
1. Prova a ripristinare un file dal backup
2. Verifica che il processo di ripristino funzioni
3. Controlla che i file siano effettivamente aggiornati

## File da includere sempre nel backup

- Documenti importanti (contratti, documenti d'identità, fatture)
- Foto e video personali
- Password manager database
- Chiavi SSH e certificati
- Codice sorgente di progetti
- Email importanti
    `
  },
  {
    slug: 'sicurezza-password-consigli',
    title: 'Come creare password sicure che ricordi davvero: guida pratica',
    date: '2026-05-09',
    category: 'Sicurezza',
    readTime: '5 min',
    excerpt: 'Le password sicure non devono essere impossibili da ricordare. Ecco le tecniche dei professionisti della sicurezza per password forti e memorabili.',
    content: `
## Il problema delle password tipiche

"Password123", il nome del cane, la data di nascita — queste sono le prime password che un attaccante prova. I dizionari di attacco contengono milioni di password comuni e le loro varianti.

Una password sicura deve essere imprevedibile, unica per ogni servizio e sufficientemente lunga.

## Quanto deve essere lunga una password?

Con hardware moderno, una password di 8 caratteri può essere forzata in poche ore. La lunghezza minima consigliata nel 2026 è **12 caratteri**, ma 16 o più è meglio.

La lunghezza batte la complessità: "cavallo-batteria-graffetta-corretta" è più sicura di "P@ssw0rd" perché è molto più lunga e casuale.

## Il metodo della passphrase

Una **passphrase** è una sequenza di parole casuali. È facile da ricordare e sicurissima:

\`\`\`
corretto-cavallo-batteria-graffetta
tavolo-elefante-luna-zaino-7
pizza-montagna-codice-verde-2026
\`\`\`

4-5 parole casuali creano una password con più di 50 bit di entropia — praticamente impossibile da forzare.

## Password manager: la soluzione definitiva

La soluzione migliore è usare un **password manager** che genera e ricorda password completamente casuali per ogni sito:

\`\`\`
# Password generata automaticamente (sicurissima)
xK9#mP2$vL8@nQ4&jR6
\`\`\`

Non devi ricordarla — la ricorda il password manager. Devi ricordare solo una password master forte per il manager.

**Opzioni consigliate:**
- **Bitwarden**: open source, gratuito, server self-hostabile
- **1Password**: ottima UX, famiglie e team
- **KeePass**: locale, nessun cloud

## Per LifetimeCloud specificamente

La password di LifetimeCloud è particolarmente importante perché **deriva la chiave crittografica** dei tuoi file. Una password debole significa file meno protetti.

Usa una passphrase di almeno 4 parole o una password casuale dal tuo manager. E salva sempre il **token di recupero** in un posto sicuro.
    `
  }
];

function PostCard({ post, onClick }) {
  // Con `href` la scheda diventa un vero <a>: si apre in una scheda nuova col
  // tasto centrale, si copia l'indirizzo col destro, e i motori di ricerca la
  // seguono. Con un onClick su un <article> niente di tutto questo funziona.
  const Guscio = post.href ? 'a' : 'article';
  const attributi = post.href
    ? { href: post.href, className: 'blog-card' }
    : { className: 'blog-card', onClick: () => onClick(post.slug) };
  return (
    <Guscio {...attributi}>
      <div className="blog-card-meta">
        <span className="blog-category">{post.category}</span>
        <span className="blog-readtime">⏱ {post.readTime}</span>
      </div>
      <h2 className="blog-card-title">{post.title}</h2>
      <p className="blog-card-excerpt">{post.excerpt}</p>
      <div className="blog-card-footer">
        <span className="blog-date">{new Date(post.date).toLocaleDateString('it-IT', { year:'numeric', month:'long', day:'numeric' })}</span>
        <span className="blog-read-more">Leggi →</span>
      </div>
    </Guscio>
  );
}

function PostContent({ post, onBack }) {
  // Simple markdown-like renderer
  function renderContent(text) {
    return text.trim().split('\n').map((line, i) => {
      if (line.startsWith('## ')) return <h2 key={i} className="blog-post-h2">{line.slice(3)}</h2>;
      if (line.startsWith('**') && line.endsWith('**')) return <p key={i}><strong>{line.slice(2,-2)}</strong></p>;
      if (line.startsWith('- ')) return <li key={i} className="blog-post-li">{renderInline(line.slice(2))}</li>;
      if (line.startsWith('```')) return null;
      if (line.trim() === '') return <br key={i}/>;
      if (line.match(/^\d\./)) return <li key={i} className="blog-post-li">{renderInline(line.replace(/^\d\.\s*/,''))}</li>;
      if (line.startsWith('|')) return null; // skip tables for now
      return <p key={i} className="blog-post-p">{renderInline(line)}</p>;
    });
  }

  function renderInline(text) {
    const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2,-2)}</strong>;
      if (part.startsWith('`') && part.endsWith('`')) return <code key={i} className="blog-inline-code">{part.slice(1,-1)}</code>;
      return part;
    });
  }

  return (
    <div className="blog-post">
      <button className="blog-back-btn" onClick={onBack}>← Torna al blog</button>
      <div className="blog-post-meta">
        <span className="blog-category">{post.category}</span>
        <span className="blog-readtime">⏱ {post.readTime}</span>
        <span className="blog-date">{new Date(post.date).toLocaleDateString('it-IT', { year:'numeric', month:'long', day:'numeric' })}</span>
      </div>
      <h1 className="blog-post-title">{post.title}</h1>
      <p className="blog-post-lead">{post.excerpt}</p>
      <div className="blog-post-body">
        {renderContent(post.content)}
      </div>
      <div className="blog-post-cta">
        <h3>Prova LifetimeCloud gratuitamente</h3>
        <p>5 GB gratis, nessuna carta di credito richiesta. I tuoi file cifrati nel browser prima ancora di raggiungere il server.</p>
        <a href="/" className="blog-cta-btn">Inizia gratis →</a>
      </div>
    </div>
  );
}

export default function BlogPage({ onBack }) {
  const [currentPost, setCurrentPost] = useState(null);

  useEffect(() => {
    // Check URL for direct post link
    const hash = window.location.hash.slice(1);
    if (hash) setCurrentPost(hash);
  }, []);

  function openPost(slug) {
    setCurrentPost(slug);
    window.scrollTo(0, 0);
    window.history.pushState({}, '', `/blog#${slug}`);
  }

  function closePost() {
    setCurrentPost(null);
    window.scrollTo(0, 0);
    window.history.pushState({}, '', '/blog');
  }

  const post = currentPost ? POSTS.find(p => p.slug === currentPost) : null;

  return (
    <div className="blog-root">
      {/* Header */}
      <header className="blog-header">
        <div className="blog-header-inner">
          <a href="/" className="blog-logo">🔐 LifetimeCloud</a>
          <nav className="blog-nav">
            <a href="/">Home</a>
            <a href="/blog" className="active">Blog</a>
          </nav>
          <a href="/" className="blog-signup-btn">Inizia gratis</a>
        </div>
      </header>

      <main className="blog-main">
        {post ? (
          <PostContent post={post} onBack={closePost}/>
        ) : (
          <>
            <div className="blog-hero">
              <div className="blog-hero-label">🔐 LifetimeCloud Blog</div>
              <h1 className="blog-hero-title">Privacy, Sicurezza e Crittografia</h1>
              <p className="blog-hero-sub">Guide pratiche e approfondimenti tecnici per proteggere davvero i tuoi dati digitali.</p>
            </div>
            <div className="blog-grid">
              {POSTS.map(post => (
                <PostCard key={post.slug} post={post} onClick={openPost}/>
              ))}
            </div>
            {/* I 52 articoli inglesi ripristinati il 13/09 (erano stati tolti
                il 30/08). Vivono come pagine statiche sotto /blog/, con i loro
                indirizzi originali intatti: qui c'e' la porta per trovarli,
                altrimenti resterebbero raggiungibili solo da chi ha gia' il
                link o arriva da un motore di ricerca. */}
            <p className="blog-archivio">
              Cerchi qualcos'altro? C'è anche l'<a href="/blog/index.html">archivio in inglese</a>, con 52 articoli su cifratura, WebDAV, backup e uso del vault.
            </p>
          </>
        )}
      </main>

      <footer className="blog-footer">
        <div className="blog-footer-inner">
          <span>© 2026 LifetimeCloud · P.IVA 12006231000 · Cloud cifrato zero-knowledge · <a href="/blog">Blog</a> · <a href="/terms">Termini</a> · <a href="/privacy">Privacy</a></span>
          <span>🔒 AES-256-GCM · PBKDF2 · Zero-knowledge</span>
        </div>
      </footer>
    </div>
  );
}
